import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Button from '@/components/ui/Button';
import Input from '@/components/ui/Input';
import MainDialogLayout from '@/components/ui/MainDialogLayout/MainDialogLayout';
import { isValidIsraeliID } from '@/helpers/israeliIdValidator';
import { EmptyFileError, FileReadError, FileReaderHelper, translateFileReadError } from '@/helpers/fileReaderHelper';
import { DocumentDtoOut } from '@/models/out/DocumentDtoOut';
import { AddFundRepresentativeDtoIn } from '@/models/in/AddFundRepresentativeDtoIn';
import { FundPersonDtoOut, WorldAccessLevel, WorldLevels } from '@/models/out/FundPersonDtoOut';
import { FULL_WORLDS, WORLDS, allAt, describeWorlds, levelIn, sameWorlds, summaryLevel, withLevel, worldsOf, worldsSummary } from '@/helpers/permissionWorlds';
import { CrownIcon, EyeIcon, EyeOffIcon, InfoIcon, PencilIcon } from './peopleIcons';
import icDonations from '@/assets/icons/donations.svg';
import icSupport from '@/assets/icons/support.svg';
import icSettingsGear from '@/assets/icons/settingsGear.svg';
import settingsStyles from '../FundSettings.module.css';
import styles from './People.module.css';
import { toHebrewName } from '@/helpers/hebrewName';

/** The same shape the donation form accepts - a local part, an @, a host with a dot. */
const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/** An Israeli phone as the fund-opening form takes it: 0 and 8-9 more digits, separators ignored. */
const PHONE_PATTERN = /^0\d{8,9}$/;

const COMPACT = '600px';

/**
 * The ID copy on a lead coordinator's request - the same limits the server holds every identity
 * upload from a coordinator to (IdentityDocumentUploads), so a file it would refuse is caught here.
 */
const ID_COPY_MAX_FILES = 5;
const ID_COPY_MAX_FILE_MB = 8;
const ID_COPY_MAX_TOTAL_MB = 18;
const ID_COPY_TYPES = ['image/jpeg', 'image/png', 'image/webp', 'application/pdf'];

type AddField = 'firstName' | 'lastName' | 'email' | 'phone' | 'israeliId' | 'city';

type AddForm = Omit<AddFundRepresentativeDtoIn, 'worlds'> & { worlds: WorldLevels };

const emptyForm: AddForm = { israeliId: '', firstName: '', lastName: '', email: '', phone: '', city: '', permissionLevel: 'Full', worlds: FULL_WORLDS };

const LEVELS: WorldAccessLevel[] = ['None', 'View', 'Edit'];

/** Each world with the sidebar's own icon for it, so the permission and the menu item look alike. */
const WORLD_ICONS = { Donations: icDonations, Supports: icSupport, Other: icSettingsGear } as const;

const noWorldOpen = (worlds: WorldLevels) => WORLDS.every((w) => levelIn(worlds, w) === 'None');

/** A person's permission in one chip: full, view only, or a mix - the mix spelled out world by world beneath it. */
export const PermissionChip = ({ person }: { person: Pick<FundPersonDtoOut, 'worlds' | 'permissionLevel'> }) => {
  const { t } = useTranslation();
  const worlds = worldsOf(person);
  const summary = worldsSummary(worlds);
  if (summary === 'full') return <span className={`${styles.chip} ${styles.chipFull}`}>{t('fundPeople.permissionFull')}</span>;
  if (summary === 'viewOnly')
    return (
      <span className={`${styles.chip} ${styles.chipViewOnly}`}>
        <EyeIcon />
        {t('fundPeople.permissionViewOnly')}
      </span>
    );
  return (
    <span className={styles.customPermission}>
      <span className={`${styles.chip} ${styles.chipCustom}`}>{t('fundPeople.permissionCustom')}</span>
      <span className={styles.worldsLine}>{describeWorlds(worlds, t)}</span>
    </span>
  );
};

interface WorldLevelsPickerProps {
  value: WorldLevels;
  onChange: (worlds: WorldLevels) => void;
  /** Keeps the radio groups of two pickers on one page apart. */
  idPrefix: string;
}

/**
 * The permission worlds (Druk, 22.10.2026): for each of «תרומות», «תמיכות» and «שאר המערכת» -
 * none, view or edit. The two buttons on top set all three at once, the levels most people get.
 */
export const WorldLevelsPicker = ({ value, onChange, idPrefix }: WorldLevelsPickerProps) => {
  const { t } = useTranslation();
  const quick = (level: WorldAccessLevel, label: string) => (
    <button
      type="button"
      className={`${styles.pillButton} ${styles.pillGhost} ${styles.quickButton}`}
      aria-pressed={sameWorlds(value, allAt(level))}
      onClick={() => onChange(allAt(level))}
    >
      {label}
    </button>
  );

  return (
    <div className={styles.worldsPicker}>
      <div className={styles.quickRow}>
        {quick('Edit', t('permissionWorlds.picker.allEdit'))}
        {quick('View', t('permissionWorlds.picker.allView'))}
      </div>
      <div className={styles.worlds}>
        {WORLDS.map((world) => (
          <div className={styles.worldRow} key={world} role="radiogroup" aria-labelledby={`${idPrefix}-${world}-name`}>
            <div className={styles.worldLabel}>
              <img src={WORLD_ICONS[world]} alt="" className={styles.worldIcon} />
              <div className={styles.worldText}>
                <span id={`${idPrefix}-${world}-name`} className={styles.worldName}>
                  {t(`permissionWorlds.names.${world}`)}
                </span>
                <span className={styles.worldHolds}>{t(`permissionWorlds.holds.${world}`)}</span>
              </div>
            </div>
            <div className={styles.segmented}>
              {LEVELS.map((level) => (
                <label key={level} className={styles.segment}>
                  <input
                    type="radio"
                    name={`${idPrefix}-${world}`}
                    checked={levelIn(value, world) === level}
                    onChange={() => onChange(withLevel(value, world, level))}
                  />
                  <span>{t(`permissionWorlds.levels.${level}`)}</span>
                </label>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

interface WorldsDialogProps {
  open: boolean;
  onClose: () => void;
  person: FundPersonDtoOut | null;
  onSubmit: (worlds: WorldLevels) => void;
  isPending: boolean;
  error: string | null;
}

/** «הרשאות» of one coordinator, from the lead coordinator's menu: a level in each world. */
export const WorldsDialog = ({ open, onClose, person, onSubmit, isPending, error }: WorldsDialogProps) => {
  const { t } = useTranslation();
  const [worlds, setWorlds] = useState<WorldLevels>(FULL_WORLDS);
  const [fieldError, setFieldError] = useState<string | null>(null);
  const current = person ? worldsOf(person) : FULL_WORLDS;

  useEffect(() => {
    if (open && person) {
      setWorlds(worldsOf(person));
      setFieldError(null);
    }
  }, [open, person]);

  const submit = () => {
    if (noWorldOpen(worlds)) return setFieldError(t('permissionWorlds.picker.atLeastOne'));
    onSubmit(worlds);
  };

  const name = person ? `${person.firstName ?? ''} ${person.lastName ?? ''}`.trim() : '';
  // Signing a support request takes edit in supports: say so before it is taken away, not after.
  const losesSigning = current.supports === 'Edit' && worlds.supports !== 'Edit';

  return (
    <MainDialogLayout
      open={open}
      onOpenChange={(o: boolean) => !o && onClose()}
      title={t('permissionWorlds.dialog.title', { name: name || t('fundPeople.unnamed') })}
      maxWidth={COMPACT}
      fitContent
    >
      <div className={styles.form}>
        <div>
          <WorldLevelsPicker
            idPrefix="person-worlds"
            value={worlds}
            onChange={(next) => {
              setWorlds(next);
              setFieldError(null);
            }}
          />
          {fieldError && <p className={settingsStyles.formError}>{fieldError}</p>}
        </div>
        {losesSigning && (
          <div className={styles.warning}>
            <InfoIcon />
            <span>{t('permissionWorlds.dialog.losesSigning')}</span>
          </div>
        )}
        {error && <p className={settingsStyles.formError}>{error}</p>}
        <div className={styles.dialogActions}>
          <Button variant="primary" onClick={submit} disabled={isPending || sameWorlds(worlds, current)}>
            {isPending ? t('loading') : t('permissionWorlds.dialog.save')}
          </Button>
          <Button variant="outline" onClick={onClose} disabled={isPending}>
            {t('fundPeople.add.cancel')}
          </Button>
        </div>
      </div>
    </MainDialogLayout>
  );
};

interface AddCoordinatorDialogProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (data: AddFundRepresentativeDtoIn) => void;
  isPending: boolean;
  error: string | null;
  /**
   * A lead coordinator's request, which the office approves (SPR-10486): the phone, the city and a
   * copy of the ID card are required too, as when a fund is opened. Without it, the office's own add.
   */
  asRequest?: boolean;
}

/**
 * «הוספת רכז לפרויקט»: name, email, Israeli ID, phone, city and permission. The ID is required -
 * every coordinator is identified by one (Druk, 22.10.2026). The office adds; a lead coordinator
 * sends the same form to the office as a request.
 */
export const AddCoordinatorDialog = ({ open, onClose, onSubmit, isPending, error, asRequest = false }: AddCoordinatorDialogProps) => {
  const { t } = useTranslation();
  const [form, setForm] = useState<AddForm>(emptyForm);
  const [fieldErrors, setFieldErrors] = useState<Partial<Record<AddField | 'worlds' | 'idCopy', string>>>({});
  const [idFiles, setIdFiles] = useState<File[]>([]);
  const [isReading, setIsReading] = useState(false);
  const idFileInput = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (open) {
      setForm(emptyForm);
      setFieldErrors({});
      setIdFiles([]);
      setIsReading(false);
    }
  }, [open]);

  const addIdFiles = (picked: FileList | null) => {
    if (!picked) return;
    let problem: string | undefined;
    const accepted: File[] = [];
    for (const file of Array.from(picked)) {
      if (file.size === 0) problem = t('emptyFileSelected', { name: file.name });
      else if (!ID_COPY_TYPES.includes(file.type)) problem = t('fundPeople.add.idCopyBadType', { name: file.name });
      else if (file.size > ID_COPY_MAX_FILE_MB * 1024 * 1024) problem = t('fundPeople.add.idCopyTooLarge', { name: file.name, max: ID_COPY_MAX_FILE_MB });
      else accepted.push(file);
    }
    const next = [...idFiles, ...accepted];
    if (next.length > ID_COPY_MAX_FILES) {
      problem = t('fundPeople.add.idCopyTooMany', { max: ID_COPY_MAX_FILES });
      next.length = ID_COPY_MAX_FILES;
    }
    setIdFiles(next);
    setFieldErrors((e) => ({ ...e, idCopy: problem }));
  };

  const setField = (field: AddField, value: string) => {
    setForm((f) => ({ ...f, [field]: value }));
    if (fieldErrors[field]) setFieldErrors((e) => ({ ...e, [field]: undefined }));
  };

  const submit = async () => {
    const errors: Partial<Record<AddField | 'worlds' | 'idCopy', string>> = {};
    if (!form.firstName.trim()) errors.firstName = t('fundPeople.add.required');
    if (!form.lastName.trim()) errors.lastName = t('fundPeople.add.required');
    const email = form.email.trim();
    if (!email) errors.email = t('fundPeople.add.required');
    else if (!EMAIL_PATTERN.test(email)) errors.email = t('fundPeople.add.invalidEmail');
    const israeliId = form.israeliId.replace(/\D/g, '');
    if (!israeliId) errors.israeliId = t('fundPeople.add.required');
    else if (!isValidIsraeliID(israeliId)) errors.israeliId = t('fundPeople.add.invalidIsraeliId');
    const phoneDigits = form.phone.replace(/[\s-]/g, '');
    if (!phoneDigits) {
      if (asRequest) errors.phone = t('fundPeople.add.required');
    } else if (asRequest && !PHONE_PATTERN.test(phoneDigits)) errors.phone = t('fundPeople.add.invalidPhone');
    if (asRequest && !(form.city ?? '').trim()) errors.city = t('fundPeople.add.required');
    if (asRequest) {
      if (idFiles.length === 0) errors.idCopy = t('fundPeople.add.idCopyRequired');
      else if (idFiles.reduce((sum, f) => sum + f.size, 0) > ID_COPY_MAX_TOTAL_MB * 1024 * 1024)
        errors.idCopy = t('fundPeople.add.idCopyTotalTooLarge', { max: ID_COPY_MAX_TOTAL_MB });
    }
    if (noWorldOpen(form.worlds)) errors.worlds = t('permissionWorlds.picker.atLeastOne');
    setFieldErrors(errors);
    if (Object.values(errors).some(Boolean)) return;

    let idDocuments: DocumentDtoOut[] | undefined;
    if (asRequest) {
      setIsReading(true);
      try {
        idDocuments = [];
        for (const file of idFiles) idDocuments.push(...(await FileReaderHelper.fileToDocumentDtoOut(file)));
      } catch (err) {
        if (err instanceof EmptyFileError || err instanceof FileReadError) {
          setFieldErrors((e) => ({ ...e, idCopy: translateFileReadError(err, t) }));
          return;
        }
        throw err;
      } finally {
        setIsReading(false);
      }
    }

    onSubmit({
      ...form,
      permissionLevel: summaryLevel(form.worlds),
      firstName: form.firstName.trim(),
      lastName: form.lastName.trim(),
      email,
      phone: form.phone.trim(),
      city: (form.city ?? '').trim(),
      israeliId: israeliId.padStart(9, '0'),
      ...(idDocuments ? { idDocuments } : {}),
    });
  };

  const field = (
    name: AddField,
    label: string,
    opts: { required?: boolean; span?: boolean; ltr?: boolean; type?: string; inputMode?: 'numeric' | 'tel' | 'email'; hint?: string } = {}
  ) => (
    <div className={`${settingsStyles.formField} ${opts.span ? styles.span2 : ''}`.trim()}>
      <label className={settingsStyles.fieldLabel} htmlFor={`add-person-${name}`}>
        {label}
        {opts.required && (
          <span className={settingsStyles.requiredMark} aria-hidden="true">
            {' '}
            *
          </span>
        )}
      </label>
      <Input
        id={`add-person-${name}`}
        className="w-full"
        type={opts.type ?? 'text'}
        inputMode={opts.inputMode}
        dir={opts.ltr ? 'ltr' : undefined}
        value={form[name] ?? ''}
        onChange={(e) => setField(name, name === 'firstName' || name === 'lastName' ? toHebrewName(e.target.value) : e.target.value)}
        error={!!fieldErrors[name]}
        required={opts.required}
      />
      {fieldErrors[name] ? (
        <p className={settingsStyles.formError}>{fieldErrors[name]}</p>
      ) : (
        opts.hint && <p className={settingsStyles.fieldHint}>{opts.hint}</p>
      )}
    </div>
  );

  return (
    <MainDialogLayout
      open={open}
      onOpenChange={(o: boolean) => !o && onClose()}
      title={t(asRequest ? 'fundPeople.add.requestTitle' : 'fundPeople.add.title')}
      maxWidth={COMPACT}
      fitContent
    >
      <div className={styles.form}>
        {asRequest && <p className={styles.dialogNote}>{t('fundPeople.add.requestIntro')}</p>}
        <div className={styles.fieldsGrid}>
          {field('firstName', t('fundPeople.add.firstName'), { required: true })}
          {field('lastName', t('fundPeople.add.lastName'), { required: true })}
          {field('email', t('fundPeople.add.email'), {
            required: true,
            span: true,
            ltr: true,
            type: 'email',
            inputMode: 'email',
            hint: t(asRequest ? 'fundPeople.add.requestEmailHint' : 'fundPeople.add.emailHint'),
          })}
          {field('phone', t('fundPeople.add.phone'), { required: asRequest, ltr: true, type: 'tel', inputMode: 'tel' })}
          {field('city', t('fundPeople.add.city'), { required: asRequest })}
          {field('israeliId', t('fundPeople.add.israeliId'), { required: true, ltr: true, inputMode: 'numeric', hint: t('fundPeople.add.israeliIdHint') })}
          {asRequest && (
            <div className={`${settingsStyles.formField} ${styles.span2}`}>
              <label className={settingsStyles.fieldLabel} htmlFor="add-person-idCopy">
                {t('fundPeople.add.idCopy')}
                <span className={settingsStyles.requiredMark} aria-hidden="true">
                  {' '}
                  *
                </span>
              </label>
              <div className="flex items-center gap-3">
                <Button variant="outline" onClick={() => idFileInput.current?.click()} disabled={isPending || isReading}>
                  {t('fundPeople.add.idCopyChoose')}
                </Button>
                {idFiles.length === 0 && <span className="body-sm-regular text-(--font-body-3)">{t('fundPeople.add.idCopyNone')}</span>}
              </div>
              <input
                ref={idFileInput}
                id="add-person-idCopy"
                type="file"
                accept={ID_COPY_TYPES.join(',')}
                multiple
                className="hidden"
                onChange={(e) => {
                  addIdFiles(e.target.files);
                  e.target.value = '';
                }}
              />
              {idFiles.length > 0 && (
                <ul className="flex flex-col gap-1 m-0 p-0 list-none">
                  {idFiles.map((file, index) => (
                    <li key={`${file.name}-${index}`} className="flex items-center gap-2 body-sm-regular">
                      <span className="truncate" dir="ltr">
                        {file.name}
                      </span>
                      <button
                        type="button"
                        className="body-sm-regular text-red-600 underline bg-transparent border-0 cursor-pointer"
                        onClick={() => setIdFiles((files) => files.filter((_, i) => i !== index))}
                        disabled={isPending || isReading}
                      >
                        {t('fundPeople.add.idCopyRemove')}
                      </button>
                    </li>
                  ))}
                </ul>
              )}
              {fieldErrors.idCopy ? (
                <p className={settingsStyles.formError}>{fieldErrors.idCopy}</p>
              ) : (
                <p className={settingsStyles.fieldHint}>{t('fundPeople.add.idCopyHint', { max: ID_COPY_MAX_FILE_MB })}</p>
              )}
            </div>
          )}
        </div>
        <fieldset className={styles.worldsFieldset}>
          <legend className={settingsStyles.fieldLabel}>{t('fundPeople.add.permission')}</legend>
          <WorldLevelsPicker
            idPrefix="add-person-worlds"
            value={form.worlds}
            onChange={(worlds) => {
              setForm((f) => ({ ...f, worlds }));
              if (fieldErrors.worlds) setFieldErrors((e) => ({ ...e, worlds: undefined }));
            }}
          />
          {fieldErrors.worlds && <p className={settingsStyles.formError}>{fieldErrors.worlds}</p>}
        </fieldset>
        {error && <p className={settingsStyles.formError}>{error}</p>}
        <div className={styles.dialogActions}>
          <Button variant="primary" onClick={submit} disabled={isPending || isReading}>
            {isPending || isReading ? t('loading') : t(asRequest ? 'fundPeople.add.requestSave' : 'fundPeople.add.save')}
          </Button>
          <Button variant="outline" onClick={onClose} disabled={isPending}>
            {t('fundPeople.add.cancel')}
          </Button>
        </div>
      </div>
    </MainDialogLayout>
  );
};

export interface PersonDetailsForm {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
}

interface EditPersonDetailsDialogProps {
  open: boolean;
  person: FundPersonDtoOut;
  onClose: () => void;
  onSubmit: (data: PersonDetailsForm) => void;
  isPending: boolean;
  error: string | null;
}

/**
 * The office correcting a person's name, email and phone from the fund panel (SPR-10343). The server
 * writes the representative contact and the site account; a new email moves the account.
 */
export const EditPersonDetailsDialog = ({ open, person, onClose, onSubmit, isPending, error }: EditPersonDetailsDialogProps) => {
  const { t } = useTranslation();
  const [form, setForm] = useState<PersonDetailsForm>({ firstName: '', lastName: '', email: '', phone: '' });
  const [fieldErrors, setFieldErrors] = useState<Partial<Record<keyof PersonDetailsForm, string>>>({});

  useEffect(() => {
    if (open) {
      setForm({ firstName: person.firstName ?? '', lastName: person.lastName ?? '', email: person.email ?? '', phone: person.phone ?? '' });
      setFieldErrors({});
    }
  }, [open, person]);

  const email = form.email.trim();
  const emailChanged = email.toLowerCase() !== (person.email ?? '').trim().toLowerCase();
  // A site account needs an address to sign in with; a contact without one may stay without.
  const emailRequired = person.hasPortalAccess || !!person.email;

  const setField = (field: keyof PersonDetailsForm, value: string) => {
    setForm((f) => ({ ...f, [field]: value }));
    if (fieldErrors[field]) setFieldErrors((e) => ({ ...e, [field]: undefined }));
  };

  const submit = () => {
    const errors: Partial<Record<keyof PersonDetailsForm, string>> = {};
    if (!form.firstName.trim()) errors.firstName = t('fundPeople.add.required');
    if (!form.lastName.trim()) errors.lastName = t('fundPeople.add.required');
    if (!email && emailRequired) errors.email = t('fundPeople.add.required');
    else if (email && !EMAIL_PATTERN.test(email)) errors.email = t('fundPeople.add.invalidEmail');
    setFieldErrors(errors);
    if (Object.values(errors).some(Boolean)) return;

    onSubmit({ firstName: form.firstName.trim(), lastName: form.lastName.trim(), email, phone: form.phone.trim() });
  };

  const field = (
    name: keyof PersonDetailsForm,
    label: string,
    opts: { required?: boolean; span?: boolean; ltr?: boolean; type?: string; hint?: string } = {}
  ) => (
    <div className={`${settingsStyles.formField} ${opts.span ? styles.span2 : ''}`.trim()}>
      <label className={settingsStyles.fieldLabel} htmlFor={`edit-person-${name}`}>
        {label}
        {opts.required && (
          <span className={settingsStyles.requiredMark} aria-hidden="true">
            {' '}
            *
          </span>
        )}
      </label>
      <Input
        id={`edit-person-${name}`}
        className="w-full"
        type={opts.type ?? 'text'}
        dir={opts.ltr ? 'ltr' : undefined}
        value={form[name]}
        onChange={(e) => setField(name, name === 'firstName' || name === 'lastName' ? toHebrewName(e.target.value) : e.target.value)}
        error={!!fieldErrors[name]}
        required={opts.required}
      />
      {fieldErrors[name] ? (
        <p className={settingsStyles.formError}>{fieldErrors[name]}</p>
      ) : (
        opts.hint && <p className={settingsStyles.fieldHint}>{opts.hint}</p>
      )}
    </div>
  );

  return (
    <MainDialogLayout open={open} onOpenChange={(o: boolean) => !o && onClose()} title={t('fundPeople.edit.title')} maxWidth={COMPACT} fitContent>
      <div className={styles.form}>
        <div className={styles.fieldsGrid}>
          {field('firstName', t('fundPeople.add.firstName'), { required: true })}
          {field('lastName', t('fundPeople.add.lastName'), { required: true })}
          {field('email', t('fundPeople.add.email'), {
            required: emailRequired,
            span: true,
            ltr: true,
            type: 'email',
            hint: emailChanged && person.hasPortalAccess ? t('fundPeople.edit.emailChangeHint') : undefined,
          })}
          {field('phone', t('fundPeople.add.phone'), { ltr: true, type: 'tel' })}
        </div>
        {error && <p className={settingsStyles.formError}>{error}</p>}
        <div className={styles.dialogActions}>
          <Button variant="primary" onClick={submit} disabled={isPending}>
            {isPending ? t('loading') : t('fundPeople.edit.save')}
          </Button>
          <Button variant="outline" onClick={onClose} disabled={isPending}>
            {t('fundPeople.add.cancel')}
          </Button>
        </div>
      </div>
    </MainDialogLayout>
  );
};

interface IsraeliIdDialogProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (israeliId: string) => void;
  fundLabel: string;
  isPending: boolean;
  error: string | null;
  /** The window every coordinator with no ID at all meets on entering a fund: its own opening line. */
  asGate?: boolean;
}

/**
 * «הוספת מספר זהות», opened from the founder's own row - not a link to «החשבון שלי»: the person
 * stays where the role is, and sees it take effect as soon as the dialog closes. Saved numbers
 * can't be changed by the person afterwards, hence the warning before saving.
 */
export const IsraeliIdDialog = ({ open, onClose, onSubmit, fundLabel, isPending, error, asGate = false }: IsraeliIdDialogProps) => {
  const { t } = useTranslation();
  const [value, setValue] = useState('');
  const [fieldError, setFieldError] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      setValue('');
      setFieldError(null);
    }
  }, [open]);

  const submit = () => {
    const digits = value.replace(/\D/g, '');
    if (!digits) return setFieldError(t('fundPeople.idDialog.required'));
    if (!isValidIsraeliID(digits)) return setFieldError(t('fundPeople.idDialog.invalid'));
    setFieldError(null);
    onSubmit(digits.padStart(9, '0'));
  };

  return (
    <MainDialogLayout open={open} onOpenChange={(o: boolean) => !o && onClose()} title={t('fundPeople.idDialog.title')} maxWidth="520px" fitContent>
      <div className={styles.form}>
        <p className={styles.dialogNote}>{t(asGate ? 'fundPeople.idGate.intro' : 'fundPeople.idDialog.intro', { fund: fundLabel })}</p>
        <div className={settingsStyles.formField}>
          <label className={settingsStyles.fieldLabel} htmlFor="founder-israeli-id">
            {t('fundPeople.idDialog.label')}
            <span className={settingsStyles.requiredMark} aria-hidden="true">
              {' '}
              *
            </span>
          </label>
          <Input
            id="founder-israeli-id"
            className={`w-full ${styles.idInput}`}
            inputMode="numeric"
            dir="ltr"
            maxLength={9}
            value={value}
            onChange={(e) => {
              setValue(e.target.value.replace(/\D/g, ''));
              setFieldError(null);
            }}
            error={!!fieldError}
          />
          {fieldError ? <p className={settingsStyles.formError}>{fieldError}</p> : <p className={settingsStyles.fieldHint}>{t('fundPeople.idDialog.hint')}</p>}
        </div>
        <div className={styles.warning}>
          <InfoIcon />
          <span>{t('fundPeople.idDialog.warning')}</span>
        </div>
        {error && <p className={settingsStyles.formError}>{error}</p>}
        <div className={styles.dialogActions}>
          <Button variant="primary" onClick={submit} disabled={isPending}>
            {isPending ? t('loading') : t('fundPeople.idDialog.save')}
          </Button>
          <Button variant="outline" onClick={onClose} disabled={isPending}>
            {t('fundPeople.idDialog.later')}
          </Button>
        </div>
      </div>
    </MainDialogLayout>
  );
};

interface HandoffDialogProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (key: string) => void;
  candidates: FundPersonDtoOut[];
  isPending: boolean;
  error: string | null;
}

/** «העברת תפקיד המייסד»: pick the coordinator who takes over. One without an ID takes over once they add one. */
export const HandoffDialog = ({ open, onClose, onSubmit, candidates, isPending, error }: HandoffDialogProps) => {
  const { t } = useTranslation();
  const [selected, setSelected] = useState<string | null>(null);

  useEffect(() => {
    if (open) setSelected(null);
  }, [open]);

  const chosen = candidates.find((c) => c.key === selected);

  return (
    <MainDialogLayout open={open} onOpenChange={(o: boolean) => !o && onClose()} title={t('fundPeople.handoff.title')} maxWidth="560px" fitContent>
      <div className={styles.form}>
        {candidates.length === 0 ? (
          <p className={styles.dialogNote}>{t('fundPeople.handoff.none')}</p>
        ) : (
          <>
            <p className={styles.dialogNote}>{t('fundPeople.handoff.intro')}</p>
            <fieldset className={styles.choiceGroup} style={{ gridTemplateColumns: '1fr' }}>
              <legend className="sr-only">{t('fundPeople.handoff.title')}</legend>
              {candidates.map((c) => (
                <label key={c.key} className={styles.choice}>
                  <input type="radio" name="handoff-target" checked={selected === c.key} onChange={() => setSelected(c.key)} />
                  <span className={styles.choiceTitle}>{`${c.firstName ?? ''} ${c.lastName ?? ''}`.trim() || t('fundPeople.unnamed')}</span>
                  <span className={styles.choiceText}>
                    {c.email && <bdi dir="ltr">{c.email}</bdi>}
                    {c.contactId != null && ` · ${t('fundPeople.handoff.hasId')}`}
                  </span>
                </label>
              ))}
            </fieldset>
            {chosen && chosen.contactId == null && (
              <div className={styles.warning}>
                <InfoIcon />
                <span>{t('fundPeople.handoff.maybeNoId')}</span>
              </div>
            )}
          </>
        )}
        {error && <p className={settingsStyles.formError}>{error}</p>}
        <div className={styles.dialogActions}>
          {candidates.length > 0 && (
            <Button variant="primary" onClick={() => selected && onSubmit(selected)} disabled={isPending || !selected}>
              {isPending ? t('loading') : t('fundPeople.handoff.save')}
            </Button>
          )}
          <Button variant="outline" onClick={onClose} disabled={isPending}>
            {t('fundPeople.handoff.cancel')}
          </Button>
        </div>
      </div>
    </MainDialogLayout>
  );
};

interface ReplaceLeadDialogProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (replacementKey: string) => void;
  lead: FundPersonDtoOut;
  /** The coordinators who stay, none of them a lead: one of them takes the role. */
  candidates: FundPersonDtoOut[];
  isPending: boolean;
  error: string | null;
}

/**
 * The office takes out a fund's only lead coordinator while other coordinators stay (3.11.2026):
 * it picks who takes the role, in the same act - the fund is not left to the automatic rule, which
 * with several coordinators and no match in APT makes all of them leads. The list is known when
 * the window opens, and the note about an ID shows from the start, so the window keeps one size.
 */
export const ReplaceLeadDialog = ({ open, onClose, onSubmit, lead, candidates, isPending, error }: ReplaceLeadDialogProps) => {
  const { t } = useTranslation();
  const [selected, setSelected] = useState<string | null>(null);

  useEffect(() => {
    if (open) setSelected(null);
  }, [open]);

  const nameOf = (p: FundPersonDtoOut) => `${p.firstName ?? ''} ${p.lastName ?? ''}`.trim() || t('fundPeople.unnamed');

  return (
    <MainDialogLayout open={open} onOpenChange={(o: boolean) => !o && onClose()} title={t('fundPeople.replaceLead.title')} maxWidth="560px" fitContent>
      <div className={styles.form}>
        <p className={styles.dialogNote}>{t('fundPeople.replaceLead.intro', { name: nameOf(lead) })}</p>
        <fieldset className={styles.choiceGroup} style={{ gridTemplateColumns: '1fr' }}>
          <legend className="sr-only">{t('fundPeople.replaceLead.title')}</legend>
          {candidates.map((c) => (
            <label key={c.key} className={styles.choice}>
              <input type="radio" name="replace-lead" checked={selected === c.key} onChange={() => setSelected(c.key)} />
              <span className={styles.choiceTitle}>{nameOf(c)}</span>
              <span className={styles.choiceText}>
                {c.email && <bdi dir="ltr">{c.email}</bdi>}
                {c.contactId != null && ` · ${t('fundPeople.handoff.hasId')}`}
              </span>
            </label>
          ))}
        </fieldset>
        {candidates.some((c) => c.contactId == null) && (
          <div className={styles.warning}>
            <InfoIcon />
            <span>{t('fundPeople.replaceLead.noIdNote')}</span>
          </div>
        )}
        {error && <p className={settingsStyles.formError}>{error}</p>}
        <div className={styles.dialogActions}>
          <Button variant="primary" onClick={() => selected && onSubmit(selected)} disabled={isPending || !selected}>
            {isPending ? t('loading') : t('fundPeople.replaceLead.save')}
          </Button>
          <Button variant="outline" onClick={onClose} disabled={isPending}>
            {t('fundPeople.handoff.cancel')}
          </Button>
        </div>
      </div>
    </MainDialogLayout>
  );
};

/**
 * «מה כל הרשאה מאפשרת», world by world since 22.10.2026: what seeing and editing each world
 * allow, what «ללא» hides, and what only a lead coordinator does. Opened from «רכזים והרשאות» and
 * from the chip beside the fund's name, so a coordinator without a lead's screen reads it too.
 * Short lists, not sentences (Chaya, 25.10.2026: it read as a wall of text); the rules that live
 * elsewhere - IDs, several lead coordinators, asking for more - are told where they happen. The
 * levels wear the chips of the people list and the worlds the sidebar's icons, so what the
 * coordinator meets outside is what this explains.
 */

export const PermissionsExplainerDialog = ({ open, onClose }: { open: boolean; onClose: () => void }) => {
  const { t } = useTranslation();

  return (
    <MainDialogLayout open={open} onOpenChange={(o: boolean) => !o && onClose()} title={t('fundPeople.explainer.title')} maxWidth="720px" fitContent>
      <div className={styles.form}>
        <table className={`${styles.explainerTable} ${styles.explainerWorlds}`}>
          <colgroup>
            <col className={styles.worldColumn} />
            <col />
            <col />
          </colgroup>
          <thead>
            <tr>
              <th aria-hidden="true" />
              <th>
                <span className={`${styles.chip} ${styles.chipViewOnly}`}>
                  <EyeIcon />
                  {t('permissionWorlds.levels.View')}
                </span>
              </th>
              <th>
                <span className={`${styles.chip} ${styles.chipFull}`}>
                  <PencilIcon />
                  {t('permissionWorlds.levels.Edit')}
                </span>
              </th>
            </tr>
          </thead>
          <tbody>
            {WORLDS.map((world) => (
              <tr key={world}>
                <th scope="row" className={styles.worldName}>
                  <span className={styles.worldLabel}>
                    <img src={WORLD_ICONS[world]} alt="" className={styles.worldIcon} />
                    {t(`permissionWorlds.names.${world}`)}
                  </span>
                </th>
                <td>{t(`fundPeople.explainer.worlds.${world}.view`)}</td>
                <td>{t(`fundPeople.explainer.worlds.${world}.edit`)}</td>
              </tr>
            ))}
          </tbody>
        </table>
        {/* What the table can't say, one short line each, lined up with the table's own columns. */}
        <dl className={styles.explainerNotes}>
          <dt>
            <span className={`${styles.chip} ${styles.chipCoordinator}`}>
              <EyeOffIcon />
              {t('fundPeople.explainer.notes.none.term')}
            </span>
          </dt>
          <dd>{t('fundPeople.explainer.notes.none.text')}</dd>
          <dt>
            <span className={`${styles.chip} ${styles.chipFounder}`}>
              <CrownIcon />
              {t('fundPeople.explainer.notes.lead.term')}
            </span>
          </dt>
          <dd>{t('fundPeople.explainer.notes.lead.text')}</dd>
        </dl>
        <p className={styles.explainerInfo}>
          <InfoIcon />
          {t('fundPeople.explainer.balance')}
        </p>
        <div className={styles.dialogActions}>
          <Button variant="primary" onClick={onClose}>
            {t('fundPeople.explainer.ok')}
          </Button>
        </div>
      </div>
    </MainDialogLayout>
  );
};
